import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import styled from 'styled-components';
import _ from 'lodash';
import { mixpanel } from '@/api/app/mixpanel';

import { replaceUri } from '@/redux/app/actions/navigation';
import { fetchInboxTickets, fetchInboxUsers } from '@/redux/app/actions/inbox';
import {
  inboxVisibleTicketsSelectorFactory,
  inboxIsLoadingSelector,
  inboxHasNextPageSelector,
  inboxLastKeySelector,
} from '@/redux/app/selectors/inbox';
import { inboxCompactModeSelector } from '@/redux/app/selectors/settings';
import { useSoloModeCheck } from '@/helpers/app/hooks';

import {
  NavigationPaneRouteParamsContext, AppNavigatorParamsContext, AppNavigatorPathContext,
} from '@/lib/navigation';
import { AppSecretsContext } from '../secrets/app-secrets';
import { TicketList } from './TicketList';
import { OutboundMessageForm } from './OutboundMessageForm';
import { InboxSearchForm } from './InboxSearchForm';
import { InboxControls } from './InboxControls';
import { InboxFilters } from './InboxFilters';
import { UserInfoContext } from '../user/user-info';
import { OnboardingContextHOC } from '../onboarding/OnboardingContextHOC';

const FILTER_PARAMS = [
  'search', 'status', 'userId', 'channel', 'from', 'to', 'order', 'unanswered', 'unaddressed', 'archived',
];

const InboxContainer = styled.div<{ wide: boolean }>`
  max-width: unset !important;
  width: ${({ wide }) => (wide ? '100%' : 'inherit')};
`;

export const Inbox = OnboardingContextHOC(() => {
  const { subdomain, ticket } = useContext(NavigationPaneRouteParamsContext);
  const { twilioCallToken, twilioAccessToken } = useContext(AppSecretsContext);
  const { inboxEnabled: wideModeEnabled, userId: currentUserId } = useContext(UserInfoContext);

  const ticketsSelector = useMemo(() => inboxVisibleTicketsSelectorFactory(subdomain), [subdomain]);
  const tickets = useSelector(ticketsSelector);
  const isLoading = useSelector(inboxIsLoadingSelector);
  const hasNextPage = useSelector(inboxHasNextPageSelector);
  const lastKey = useSelector(inboxLastKeySelector);
  const compactMode = useSelector(inboxCompactModeSelector);
  const soloMode = useSoloModeCheck();

  const newMessageFormRef = useRef<OutboundMessageForm>();
  const handleNewButtonClick = useCallback(() => {
    newMessageFormRef.current.show();
  }, [newMessageFormRef]);

  const params = useContext(AppNavigatorParamsContext);
  const emptyParams = FILTER_PARAMS.reduce((acc, p) => Object.assign(acc, { [p]: '' }), {});
  const assignedParams = Object.assign(emptyParams, { userId: (soloMode ? currentUserId : ''), ...params });
  const assignedParamValues = Object.values(assignedParams);

  const path = useContext(AppNavigatorPathContext);
  const wideMode = _.last(path.asList) === 'inbox';

  const dispatch = useDispatch();
  useEffect(() => {
    if (ticket) return;
    if (wideModeEnabled && !wideMode) {
      dispatch(replaceUri({ path: `/accounts/${subdomain}/inbox`, params }));
    } else if (!wideModeEnabled && wideMode) {
      dispatch(replaceUri({ path: `/accounts/${subdomain}/tickets`, params }));
    }
  }, [wideMode, dispatch]);

  useEffect(() => {
    dispatch(fetchInboxUsers(subdomain));
  }, [subdomain]);

  useEffect(() => {
    window.initTwilio(twilioCallToken, twilioAccessToken, params.debug === 'true');
    window.setCallerData();
  }, [twilioCallToken, twilioAccessToken, params.debug]);

  useEffect(() => {
    mixpanel.identify(currentUserId);
  }, [currentUserId]);

  const loadFirstPage = useCallback(() => {
    dispatch(fetchInboxTickets({ subdomain, limit: 20, ...assignedParams }));
  }, [dispatch, subdomain, ...assignedParamValues]);
  const loadNextPage = useCallback(() => {
    dispatch(fetchInboxTickets({ subdomain, after: lastKey, limit: 20, ...assignedParams }));
  }, [dispatch, subdomain, lastKey, ...assignedParamValues]);

  const filtersRef = useRef<InboxFilters>();

  const handleToggleFiltersOff = useCallback(() => {
    filtersRef.current.toggleOff();
  }, [filtersRef]);

  const handleToggleFilters = useCallback(() => {
    filtersRef.current.toggle();
  }, [filtersRef]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const hasActiveFilters = FILTER_PARAMS.some((p) => {
    if (p === 'search') return false;
    if (p === 'userId' && soloMode) return assignedParams[p] !== currentUserId;
    return assignedParams[p];
  });

  const inboxControls = (
    <InboxControls
      handleRefresh={loadFirstPage}
      handleToggleFilters={handleToggleFilters}
      handleToggleFiltersOff={handleToggleFiltersOff}
      hasActiveFilters={hasActiveFilters}
    />
  );
  const inboxSearchAndNew = (
    <>
      <InboxSearchForm />
      <div id="create-ticket-button">
        <button type="button" id="outbound-modal-btn" onClick={handleNewButtonClick}>+ New</button>
      </div>
    </>
  );

  return (
    <InboxContainer
      wide={wideMode}
      id={wideMode ? 'inbox-wide' : 'inbox'}
      className={classNames(
        'ui-column-2', 'flex', 'inbox', ticket && 'not-mobile', compactMode && 'compact',
      )}
    >
      <div className="head">
        <div className="row">
          {wideMode && inboxControls}
          {wideMode ? <div className="search-container">{inboxSearchAndNew}</div> : inboxSearchAndNew}
          <OutboundMessageForm ref={newMessageFormRef} />
        </div>
        {!wideMode && inboxControls}
        <InboxFilters ref={filtersRef} />
      </div>
      <TicketList
        wide={wideMode}
        compact={compactMode}
        uuids={tickets}
        activeUuid={ticket}
        hasNextPage={hasNextPage ?? true}
        loadNextPage={loadNextPage}
        isNextPageLoading={isLoading}
      />
      <div id="tickets-footer" />
    </InboxContainer>
  );
});
